import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CSV_FIELDS, CSV_FIELD_LABEL, PHONEBOOK_IMPORT_BATCH, contactsFromCsv, guessCsvColumns, importKind,
  importable, parseCsv, parsePhonebookXml, parseVcards,
  type CsvField, type ImportedContact, type Phonebook, type PhonebookImportResult,
} from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { phonebookKeys } from '../../lib/api/hooks-phonebook.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';

/**
 * Uploading contacts (docs/32 decision 3): a CSV with its columns matched here, vCards, or a
 * Yealink or Fanvil phonebook. The file is read in the browser, shown before anything is kept,
 * and sent to the open book in batches.
 */

interface Loaded {
  name: string;
  kind: 'csv' | 'vcard' | 'xml';
  /** A CSV's rows; the other kinds are read whole. */
  rows: string[][];
  contacts: ImportedContact[];
}

const PREVIEW = 5;
const MAX_ROWS = 20_000;

const describe = (c: ImportedContact) =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company || '';

export function ContactUpload({ tenantId, book, departmentId, extensionId, where, onDone }: {
  tenantId: string;
  book: Phonebook;
  departmentId: string | null;
  extensionId: string | null;
  /** "the account's phonebook", for the button. */
  where: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<Loaded | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [header, setHeader] = useState(true);
  const [columns, setColumns] = useState<Array<CsvField | null>>([]);
  const [sent, setSent] = useState<{ done: number; result: PhonebookImportResult } | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const read = async (f: File) => {
    setProblem(null); setSent(null); setError(null); setFile(null);
    const text = await f.text();
    const kind = importKind(f.name, text.slice(0, 200));
    if (!kind) { setProblem('That file is not a CSV, a vCard or a phonebook XML.'); return; }
    if (kind === 'csv') {
      const rows = parseCsv(text);
      if (rows.length === 0) { setProblem('That file has no rows.'); return; }
      const guessed = guessCsvColumns(rows[0]!);
      const named = guessed.some((g) => g !== null);
      setHeader(named);
      setColumns(named ? guessed : rows[0]!.map(() => null));
      setFile({ name: f.name, kind, rows: rows.slice(0, MAX_ROWS + 1), contacts: [] });
      return;
    }
    const contacts = (kind === 'vcard' ? parseVcards(text) : parsePhonebookXml(text)).slice(0, MAX_ROWS);
    if (contacts.length === 0) { setProblem('No contacts were found in that file.'); return; }
    setFile({ name: f.name, kind, rows: [], contacts });
  };

  const contacts = useMemo(() => {
    if (!file) return [];
    if (file.kind !== 'csv') return file.contacts;
    return contactsFromCsv(header ? file.rows.slice(1) : file.rows, columns);
  }, [file, header, columns]);
  const keep = contacts.filter(importable);
  const width = file?.kind === 'csv' ? Math.max(...file.rows.slice(0, 50).map((r) => r.length)) : 0;

  const upload = async () => {
    setSending(true); setError(null);
    const total: PhonebookImportResult = {
      created: 0, companies_created: 0, skipped: contacts.length - keep.length, numbers_dropped: 0, companies_dropped: 0,
    };
    try {
      for (let i = 0; i < keep.length; i += PHONEBOOK_IMPORT_BATCH) {
        const batch = keep.slice(i, i + PHONEBOOK_IMPORT_BATCH).map((c) => ({ ...c, numbers: c.numbers.slice(0, 8) }));
        const r = await api.post<PhonebookImportResult>(`/v1/tenants/${tenantId}/phonebook/import`, {
          book, department_id: departmentId, extension_id: extensionId, contacts: batch,
        });
        total.created += r.created;
        total.companies_created += r.companies_created;
        total.skipped += r.skipped;
        total.numbers_dropped += r.numbers_dropped;
        total.companies_dropped += r.companies_dropped;
        setSent({ done: Math.min(i + batch.length, keep.length), result: { ...total } });
      }
      setFile(null);
    } catch (e) {
      setError(e);
    } finally {
      setSending(false);
      void qc.invalidateQueries({ queryKey: phonebookKeys.all(tenantId) });
    }
  };

  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
      <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input type="file" accept=".csv,.txt,.vcf,.xml" aria-label="Contacts file" disabled={sending}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); }} />
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>
          A CSV from a spreadsheet, Outlook or Google; vCards (.vcf); or a Yealink or Fanvil phonebook (.xml).
        </span>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm" disabled={sending} onClick={onDone}>Close</button>
      </div>

      {problem && <div className="arc-error" style={{ marginTop: 10 }}>{problem}</div>}

      {file?.kind === 'csv' && (
        <div style={{ marginTop: 12 }}>
          <div className="arc-row" style={{ gap: 8, marginBottom: 8 }}>
            <Switch id="csv-header" checked={header} onChange={setHeader} disabled={sending} />
            <label htmlFor="csv-header" style={{ fontSize: 12.5, cursor: 'pointer' }}>The first row names the columns</label>
          </div>
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Column</th><th>First value</th><th style={{ width: 200 }}>Is</th></tr>
              </thead>
              <tbody>
                {Array.from({ length: width }, (_, i) => (
                  <tr key={i}>
                    <td>{header ? file.rows[0]?.[i] || `Column ${i + 1}` : `Column ${i + 1}`}</td>
                    <td className="arc-muted">{(header ? file.rows[1] : file.rows[0])?.[i] ?? ''}</td>
                    <td>
                      <select className="arc-select" value={columns[i] ?? ''} disabled={sending}
                        aria-label={`What column ${i + 1} is`}
                        onChange={(e) => setColumns((cs) => {
                          const next = [...cs];
                          while (next.length < width) next.push(null);
                          const field = (e.target.value || null) as CsvField | null;
                          // One column per field: a field chosen here leaves the column that had it.
                          return next.map((f, j) => (j === i ? field : f === field && field !== null ? null : f));
                        })}>
                        <option value="">Not used</option>
                        {CSV_FIELDS.map((f) => <option key={f} value={f}>{CSV_FIELD_LABEL[f]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {file && (
        <div style={{ marginTop: 12 }}>
          <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <strong style={{ fontSize: 12.5 }}>{`${keep.length} contact${keep.length === 1 ? '' : 's'} to add`}</strong>
            {contacts.length > keep.length && (
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                {`${contacts.length - keep.length} row${contacts.length - keep.length === 1 ? ' has' : 's have'} no name or no number, and will be left out.`}
              </span>
            )}
          </div>
          {keep.length > 0 && (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Name</th><th>Company</th><th>Numbers</th><th>Email</th></tr></thead>
                <tbody>
                  {keep.slice(0, PREVIEW).map((c, i) => (
                    <tr key={i}>
                      <td>{describe(c)}</td>
                      <td>{c.first_name || c.last_name ? c.company ?? '' : ''}</td>
                      <td className="arc-mono">{c.numbers.map((n) => n.number).join(', ')}</td>
                      <td>{c.email ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="arc-row" style={{ gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="arc-btn arc-btn-primary" disabled={sending || keep.length === 0}
              onClick={() => void upload()}>
              {sending
                ? `Adding… ${sent?.done ?? 0} of ${keep.length}`
                : `Add ${keep.length} to ${where}`}
            </button>
          </div>
        </div>
      )}

      {error !== null && <div style={{ marginTop: 10 }}><ErrorBanner error={error} /></div>}
      {sent && !sending && (
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 10, marginBottom: 0 }}>
          {[
            `Added ${sent.result.created} contact${sent.result.created === 1 ? '' : 's'}`,
            sent.result.companies_created > 0 && `and ${sent.result.companies_created} compan${sent.result.companies_created === 1 ? 'y' : 'ies'}`,
          ].filter(Boolean).join(' ')}.
          {sent.result.skipped > 0 && ` ${sent.result.skipped} left out.`}
          {sent.result.numbers_dropped > 0 && ` ${sent.result.numbers_dropped} number${sent.result.numbers_dropped === 1 ? ' was' : 's were'} not a number and left off.`}
          {sent.result.companies_dropped > 0 && ` ${sent.result.companies_dropped} company name${sent.result.companies_dropped === 1 ? ' is' : 's are'} not in Companies and left off.`}
        </p>
      )}
    </div>
  );
}
